import { create } from 'zustand';
import { Photo, Overlay, EditorStep, OverlayConfig } from '../types';

export interface CropState {
  x: number;
  y: number;
  width: number;
  height: number;
  enabled: boolean;
}

export interface FilterState {
  brightness: number;    // -1 to 1 (Konva Brighten)
  contrast: number;      // -100 to 100 (Konva Contrast)
  saturation: number;    // -2 to 2 (Konva HSL)
  grayscale: boolean;    // Konva Grayscale
}

/**
 * Per-format reframe offset.
 * focusX/focusY range from 0 to 1 where 0.5 = centered.
 *   0 = show left/top edge, 1 = show right/bottom edge.
 * Maps directly to CSS object-position and render pipeline.
 */
export interface ReframeOffset {
  focusX: number; // 0..1, default 0.5
  focusY: number; // 0..1, default 0.5
}

interface EditorState {
  isOpen: boolean;
  photo: Photo | null;
  overlays: Overlay[];
  step: EditorStep;
  /** Optional callback so the opener (e.g. the gallery) can reflect a highlight
   *  toggle made from the post-export prompt without a refetch. */
  onHighlightChange?: ((photoId: string, isHighlight: boolean) => void) | null;

  // Filters
  filters: FilterState;

  // Crop
  crop: CropState;

  // Branding overlay — always applied during export, set from admin panel defaults.
  brandingOverlayConfig: OverlayConfig | null;
  // User's additional overlay choice (on top of branding).
  overlayConfig: OverlayConfig | null;

  // Export selection
  selectedPlatforms: string[];
  selectedFormats: string[]; // e.g. "Instagram-Square"
  exportName: string;

  // Per-format reframe offsets: key = "Platform-Label" e.g. "Instagram-Stories"
  reframeOffsets: Record<string, ReframeOffset>;

  // Rotation (degrees, clockwise)
  rotation: 0 | 90 | 180 | 270;

  // Actions
  openEditor: (
    photo: Photo,
    overlays: Overlay[],
    onHighlightChange?: (photoId: string, isHighlight: boolean) => void,
    defaultOverlayConfig?: OverlayConfig | null,
  ) => void;
  closeEditor: () => void;
  /** Mark the editor's photo as highlighted in-store + notify the opener. */
  setPhotoHighlighted: (isHighlight: boolean) => void;
  setStep: (step: EditorStep) => void;
  nextStep: () => void;
  prevStep: () => void;

  setFilters: (filters: Partial<FilterState>) => void;
  resetFilters: () => void;

  setCrop: (crop: Partial<CropState>) => void;
  resetCrop: () => void;

  setOverlayConfig: (config: OverlayConfig | null) => void;
  updateOverlayConfig: (partial: Partial<OverlayConfig>) => void;

  setSelectedPlatforms: (platforms: string[]) => void;
  toggleFormat: (format: string) => void;
  setSelectedFormats: (formats: string[]) => void;
  setExportName: (name: string) => void;

  setReframeOffset: (formatKey: string, offset: ReframeOffset) => void;
  getReframeOffset: (formatKey: string) => ReframeOffset;

  rotateLeft: () => void;
  rotateRight: () => void;

  // Load a saved config
  loadConfig: (config: {
    filters: FilterState;
    overlay: OverlayConfig | null;
    selectedPlatforms: string[];
    selectedFormats: string[];
  }) => void;
}

const STEPS: EditorStep[] = ['preset', 'adjust', 'crop', 'overlay', 'export'];

const defaultFilters: FilterState = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  grayscale: false,
};

const defaultCrop: CropState = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  enabled: false,
};

const DEFAULT_REFRAME: ReframeOffset = { focusX: 0.5, focusY: 0.5 };

export const useEditorStore = create<EditorState>((set, get) => ({
  isOpen: false,
  photo: null,
  overlays: [],
  step: 'adjust',
  filters: defaultFilters,
  crop: defaultCrop,
  brandingOverlayConfig: null,
  overlayConfig: null,
  selectedPlatforms: [],
  selectedFormats: [],
  exportName: '',
  reframeOffsets: {},
  rotation: 0,

  openEditor: (photo, overlays, onHighlightChange, defaultOverlayConfig) =>
    set({
      isOpen: true,
      photo,
      overlays,
      onHighlightChange: onHighlightChange ?? null,
      step: 'preset',
      filters: defaultFilters,
      crop: defaultCrop,
      brandingOverlayConfig: defaultOverlayConfig ?? null,
      overlayConfig: null,
      selectedPlatforms: [],
      selectedFormats: [],
      exportName: photo.originalFilename.replace(/\.[^.]+$/, ''),
      reframeOffsets: {},
      rotation: 0,
    }),

  closeEditor: () =>
    set({
      isOpen: false,
      photo: null,
      onHighlightChange: null,
      step: 'preset',
      filters: defaultFilters,
      crop: defaultCrop,
      brandingOverlayConfig: null,
      overlayConfig: null,
      selectedPlatforms: [],
      selectedFormats: [],
      exportName: '',
      reframeOffsets: {},
      rotation: 0,
    }),

  setPhotoHighlighted: (isHighlight) => {
    const { photo, onHighlightChange } = get();
    if (!photo) return;
    set({ photo: { ...photo, isHighlight } });
    onHighlightChange?.(photo._id, isHighlight);
  },

  setStep: (step) => set({ step }),

  nextStep: () => {
    const idx = STEPS.indexOf(get().step);
    if (idx < STEPS.length - 1) set({ step: STEPS[idx + 1] });
  },

  prevStep: () => {
    const idx = STEPS.indexOf(get().step);
    if (idx > 0) set({ step: STEPS[idx - 1] });
  },

  setFilters: (filters) =>
    set((state) => ({ filters: { ...state.filters, ...filters } })),

  resetFilters: () => set({ filters: defaultFilters }),

  setCrop: (crop) =>
    set((state) => ({ crop: { ...state.crop, ...crop } })),

  resetCrop: () => set({ crop: defaultCrop }),

  setOverlayConfig: (config) => set({ overlayConfig: config }),

  updateOverlayConfig: (partial) =>
    set((state) => ({
      overlayConfig: state.overlayConfig
        ? { ...state.overlayConfig, ...partial }
        : null,
    })),

  setSelectedPlatforms: (platforms) => set({ selectedPlatforms: platforms }),

  toggleFormat: (format) =>
    set((state) => ({
      selectedFormats: state.selectedFormats.includes(format)
        ? state.selectedFormats.filter((f) => f !== format)
        : [...state.selectedFormats, format],
    })),

  setSelectedFormats: (formats) => set({ selectedFormats: formats }),

  setExportName: (name) => set({ exportName: name }),

  setReframeOffset: (formatKey, offset) =>
    set((state) => ({
      reframeOffsets: { ...state.reframeOffsets, [formatKey]: offset },
    })),

  getReframeOffset: (formatKey) => {
    return get().reframeOffsets[formatKey] || DEFAULT_REFRAME;
  },

  rotateLeft: () =>
    set((s) => ({
      rotation: (((s.rotation - 90 + 360) % 360) as 0 | 90 | 180 | 270),
      crop: defaultCrop,
    })),

  rotateRight: () =>
    set((s) => ({
      rotation: (((s.rotation + 90) % 360) as 0 | 90 | 180 | 270),
      crop: defaultCrop,
    })),

  loadConfig: (config) =>
    set({
      filters: config.filters,
      overlayConfig: config.overlay,
      selectedPlatforms: config.selectedPlatforms,
      selectedFormats: config.selectedFormats,
      reframeOffsets: {},
    }),
}));
